<template>
  <PbDialog
    v-if="modes && modes.length"
    :dialog-title="translate($route.meta.title)"
    :loading="loading"
    :close-to="closeTo"
  >
    <ul v-if="modes && modes.length">
      <template
        v-for="mode in modes"
        :key="mode.data._uid"
      >
        <PbLink
          v-slot="{
            navigate, href, isActive,
          }"
          custom
          :to="mode.link"
        >
          <!-- Other available props from v-slot: href, route -->
          <ModeItem
            :mode="mode"
            :href="href"
            :active="isActive"
            :drag-drop-context="dragDropContext"
            :deletable="canDeleteModes"
            @navigate="navigate"
          />
        </PbLink>
      </template>
    </ul>

    <template #footer>
      <ButtonWithLoader
        :label="translate('pb2.manageModesView.addModeLabel')"
        :disabled="readonly"
        @click="addMode"
      />
    </template>
  </PbDialog>
</template>

<script setup>
import { computed } from 'vue'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import PbDialog from '@/components/PbDialog.vue'
import useRouterStore from '@/stores/router.store'
import routeNames from '@/router/names'
import useTranslate from '@/composables/useTranslate'
import {
  useCurrent,
  useObject,
  useModeStatus,
  useReadonly,
} from '@/composables/useProfile'
import ModeItem from '@/components/ModeItem/ModeItem.vue'
import PbLink from '@/components/PbLink.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import { useDragDropContext } from '@/composables/useDragDrop'
import useModeStore from '@/stores/mode.store'
import useProfileStore from '@/stores/profile.store'
import useIsGlobal from '@/composables/useIsGlobal'

const routerStore = useRouterStore()
const profileStore = useProfileStore()
const { translate } = useTranslate()

const loading = useModeStatus().pending

const { modes } = useCurrent()
const { isGlobal } = useIsGlobal()
const readonly = useReadonly()

const object = useObject()

const modeStore = useModeStore()

const closeTo = computed(() => {
  const currentRoute = routerStore.route
  const { profileUid } = profileStore
  const currentObject = object.value
  if (loading.value) {
    return currentRoute
  }
  return currentObject ? currentObject.link : {
    name: routeNames.GlobalEditor,
    params: {
      profileUid,
    },
  }
})

const canDeleteModes = computed(() => (
  // Prevent deleting modes when there is less than 2 modes
  modes.value && modes.value.length > 1
))

function addMode() {
  return modeStore.create({
    query: true,
    redirect: !isGlobal.value,
  })
}

const dragDropContext = useDragDropContext({
  onDrop(draggedModeData, dropzoneData) {
    const { before, after, mode: dropzoneMode } = dropzoneData
    if (draggedModeData.data._uid !== dropzoneMode.data._uid) {
      // If the mode is not moving to it's own position
      const sourceOrder = draggedModeData.data._order
      let destinationOrder = dropzoneMode.data._order
      if (sourceOrder < destinationOrder && before) {
        // If the mode we're moving was before the mode we're moving to
        // and we dropped before the mode we're moving to...
        // Remove 1 to the destination order, to take into account all orders shifting
        // because of the moving mode
        destinationOrder -= 1
      }
      if (sourceOrder > destinationOrder && after) {
        // If the mode we're moving was after the mode we're moving to
        // and we dropped after the mode we're moving to...
        // Add 1 to the order, to take into account all orders shifting
        // because of the moving mode
        destinationOrder += 1
      }
      if (destinationOrder !== sourceOrder) {
        dev.log(`Moving mode ${draggedModeData.data.name} to order ${destinationOrder}`)
        modeStore.move({ _uid: draggedModeData.data._uid, _order: destinationOrder })
      }
    }
  },
})
</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '/src/styles/_pb.scss';

</style>
